import { create } from 'zustand'
import type { SelectOption } from '@/components/SearchableSelect'
import {
  ALL_STORES_ID,
  type CloverDraftResponse,
  type CloverFetchResponse,
} from '@/services/cloverReports'

export const CLOVER_REPORTS_CACHE_KEY = 'bookkeeper:clover-reports-cache'
export const CLOVER_REPORTS_CACHE_TTL_MS = 60 * 60 * 1000

export type CloverStoreResult = {
  storeName: string
  fetchData: CloverFetchResponse
  draft: CloverDraftResponse
  gift_card_classifications: Record<string, string>
}

type CachedPayload = {
  savedAt: number
  store: string | null
  reportDate: string
  results: CloverStoreResult[]
  showAuthRecovery: boolean
}

interface CloverReportsState {
  store: string | null
  reportDate: string
  results: CloverStoreResult[]
  showAuthRecovery: boolean
  cachedAt: number | null

  setStore: (store: string | null) => void
  setReportDate: (reportDate: string) => void
  setResults: (results: CloverStoreResult[]) => void
  updateResult: (draftId: number, patch: Partial<CloverStoreResult>) => void
  setShowAuthRecovery: (show: boolean) => void
  reset: () => void
  isCacheValid: () => boolean
  expireIfStale: () => boolean
}

function normalizeStoreResult(result: CloverStoreResult): CloverStoreResult {
  return {
    ...result,
    gift_card_classifications:
      result.gift_card_classifications ??
      result.draft.gift_card_recon?.classifications ??
      {},
  }
}

function normalizeResults(results: CloverStoreResult[]): CloverStoreResult[] {
  return results.map(normalizeStoreResult)
}

function loadFromStorage(): CachedPayload | null {
  try {
    const raw = localStorage.getItem(CLOVER_REPORTS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    if (!parsed?.savedAt || !Array.isArray(parsed.results)) return null
    if (Date.now() - parsed.savedAt >= CLOVER_REPORTS_CACHE_TTL_MS) {
      localStorage.removeItem(CLOVER_REPORTS_CACHE_KEY)
      return null
    }
    return {
      ...parsed,
      results: normalizeResults(parsed.results),
    }
  } catch {
    localStorage.removeItem(CLOVER_REPORTS_CACHE_KEY)
    return null
  }
}

function saveToStorage(state: {
  cachedAt: number | null
  store: string | null
  reportDate: string
  results: CloverStoreResult[]
  showAuthRecovery: boolean
}): void {
  if (state.cachedAt == null) return
  const payload: CachedPayload = {
    savedAt: state.cachedAt,
    store: state.store,
    reportDate: state.reportDate,
    results: state.results,
    showAuthRecovery: state.showAuthRecovery,
  }
  localStorage.setItem(CLOVER_REPORTS_CACHE_KEY, JSON.stringify(payload))
}

function touchCache(state: CloverReportsState): CloverReportsState {
  const cachedAt = Date.now()
  const next = { ...state, cachedAt }
  saveToStorage(next)
  return next
}

const initialCache = loadFromStorage()

export const useCloverReportsStore = create<CloverReportsState>((set, get) => ({
  store: initialCache?.store ?? ALL_STORES_ID,
  reportDate: initialCache?.reportDate ?? '',
  results: initialCache?.results ?? [],
  showAuthRecovery: initialCache?.showAuthRecovery ?? false,
  cachedAt: initialCache?.savedAt ?? null,

  setStore: (store) =>
    set((state) => touchCache({ ...state, store })),

  setReportDate: (reportDate) =>
    set((state) => touchCache({ ...state, reportDate })),

  setResults: (results) =>
    set((state) => touchCache({ ...state, results: normalizeResults(results) })),

  updateResult: (draftId, patch) =>
    set((state) => {
      const results = state.results.map((result) =>
        result.draft.id === draftId
          ? normalizeStoreResult({ ...result, ...patch })
          : result
      )
      return touchCache({ ...state, results })
    }),

  setShowAuthRecovery: (showAuthRecovery) =>
    set((state) => touchCache({ ...state, showAuthRecovery })),

  reset: () => {
    localStorage.removeItem(CLOVER_REPORTS_CACHE_KEY)
    set({
      store: ALL_STORES_ID,
      reportDate: '',
      results: [],
      showAuthRecovery: false,
      cachedAt: null,
    })
  },

  isCacheValid: () => {
    const { cachedAt } = get()
    if (cachedAt == null) return false
    return Date.now() - cachedAt < CLOVER_REPORTS_CACHE_TTL_MS
  },

  expireIfStale: () => {
    if (get().isCacheValid()) return false
    get().reset()
    return true
  },
}))

export const DEFAULT_CLOVER_STORE_OPTIONS: SelectOption[] = [
  { id: ALL_STORES_ID, label: 'All Stores' },
  { id: 'Highland Park', label: 'Highland Park' },
  { id: 'River Oaks', label: 'River Oaks' },
]
